import { Injectable } from '@angular/core'
import { HttpEventType } from '@angular/common/http'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { NotificationType } from 'angular2-notifications'
import { of } from 'rxjs'
import { catchError, delay, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators'
import { AppNotificationService } from '../../services/app-notification.service'
import { AppSpinnerService } from '../../services/app-spinner.service'
import { AppCoreService } from '../../services/app-core.service'
import { AppBasicPerson } from '../../models/interfaces/app-basic-person.interface'
import { AppBasicPartner } from '../../models/interfaces/app-basic-partner.interface'
import { Todo, TodoTableData } from '../../../features/feature-partners/models/interfaces/table-todo-data.interface'
import { ProjectService } from './project.service'
import { AppReportingPeriodResponse } from '../../models/interfaces/app-reporting-period.interface'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { AppFileDownloadService } from '../../services/app-file-download.service'
import { getProject } from '../stage/stage.selectors'
import { AppState } from '../storage'
import * as ProjectActions from './project.actions'

@Injectable()
export class ProjectEffects {
   exportProject$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.exportProject),
         switchMap((action) =>
            this.projectService.exportProject().pipe(
               map((response) => ProjectActions.exportProjectSuccess({ response })),
               catchError((error) => of(ProjectActions.exportProjectFailed({ error })))
            )
         )
      )
   )

   saveProjectFile$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(ProjectActions.exportProjectSuccess),
            withLatestFrom(this.store.select(getProject)),
            tap(([action, project]) => this.fileDownloadService.saveProject(action.response, project))
         ),
      { dispatch: false }
   )

   closeExportProgress$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.exportProjectSuccess),
         filter(({ response }) => response.type === HttpEventType.Response),
         delay(1000),
         map(() => ProjectActions.closeExportProgress())
      )
   )

   loadPartnerList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadPartnerList),
         switchMap((action) =>
            this.projectService.getPartnerList().pipe(
               map((response: AppBasicPartner[]) => ProjectActions.loadPartnerListSuccess({ response })),
               catchError((error) => of(ProjectActions.loadPartnerListFailed({ error })))
            )
         )
      )
   )

   loadPartner$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadPartner),
         switchMap((action) =>
            this.projectService.getPartnerById(action.id).pipe(
               map((response: AppBasicPartner) => ProjectActions.loadPartnerSuccess({ response })),
               catchError((error) => of(ProjectActions.loadPartnerFailed({ error })))
            )
         )
      )
   )

   addPartner$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.addPartner),
         switchMap((action) =>
            this.projectService.insertPartner(action.data).pipe(
               map(() => ProjectActions.addPartnerSuccess()),
               catchError((error) => of(ProjectActions.addPartnerFailed({ error })))
            )
         )
      )
   )

   updatePartner$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.updatePartner),
         switchMap((action) =>
            this.projectService.updatePartner(action.id, action.data).pipe(
               map(() => ProjectActions.updatePartnerSuccess()),
               catchError((error) => of(ProjectActions.updatePartnerFailed({ error })))
            )
         )
      )
   )

   deletePartner$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.deletePartner),
         switchMap((action) =>
            this.projectService.deletePartner(action.id).pipe(
               map(() => ProjectActions.deletePartnerSuccess()),
               catchError((error) => of(ProjectActions.deletePartnerFailed({ error })))
            )
         )
      )
   )

   updatePartnerOrder$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.updatePartnerOrder),
         switchMap((action) =>
            this.projectService.changePartnerOrder(action.data).pipe(
               map((response: AppBasicPartner[]) => ProjectActions.updatePartnerOrderSuccess({ response })),
               catchError((error) => of(ProjectActions.updatePartnerOrderFailed({ error })))
            )
         )
      )
   )

   loadPerson$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadPerson),
         switchMap((action) =>
            this.projectService.getPersonById(action.id).pipe(
               map((response: AppBasicPerson) => ProjectActions.loadPersonSuccess({ response })),
               catchError((error) => of(ProjectActions.loadPersonFailed({ error })))
            )
         )
      )
   )

   searchPersonsByEmail$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.searchPersonsByEmail),
         switchMap((action) =>
            this.projectService.searchPersonsByEmail(action.email).pipe(
               takeUntil(this.actions$.pipe(ofType(ProjectActions.clearPersonEmailSearch))),
               map((response: AppBasicPerson[]) => ProjectActions.searchPersonsByEmailSuccess({ response })),
               catchError((error) => of(ProjectActions.searchPersonsByEmailFailed({ error })))
            )
         )
      )
   )

   addPerson$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.addPerson),
         switchMap((action) =>
            this.projectService.insertPerson(action.partnerId, action.data).pipe(
               map(() => ProjectActions.addPersonSuccess()),
               catchError((error) => of(ProjectActions.addPersonFailed({ error })))
            )
         )
      )
   )

   updatePerson$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.updatePerson),
         switchMap((action) =>
            this.projectService.updatePerson(action.partnerId, action.id, action.data).pipe(
               map(() => ProjectActions.updatePersonSuccess()),
               catchError((error) => of(ProjectActions.updatePersonFailed({ error })))
            )
         )
      )
   )

   deletePerson$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.deletePerson),
         switchMap((action) =>
            this.projectService.deletePerson(action.partnerId, action.id).pipe(
               map(() => ProjectActions.deletePersonSuccess()),
               catchError((error) => of(ProjectActions.deletePersonFailed({ error })))
            )
         )
      )
   )

   loadProjectTodo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadProjectTodo),
         switchMap((action) =>
            this.projectService.getProjectTodo().pipe(
               map((response: TodoTableData) => ProjectActions.loadProjectTodoSuccess({ response })),
               catchError((error) => of(ProjectActions.loadProjectTodoFailed({ error })))
            )
         )
      )
   )

   reloadProjectTodoAfterMutation$ = createEffect(() =>
      this.actions$.pipe(
         ofType(
            ProjectActions.addTodoSuccess,
            ProjectActions.updateTodoSuccess,
            ProjectActions.deleteTodoSuccess,
            ProjectActions.updateTodoPartnerStatusSuccess
         ),
         map(() => ProjectActions.loadProjectTodo())
      )
   )

   reloadPartnerListAfterMutation$ = createEffect(() =>
      this.actions$.pipe(
         ofType(
            ProjectActions.addPartnerSuccess,
            ProjectActions.updatePartnerSuccess,
            ProjectActions.deletePartnerSuccess,
            ProjectActions.addPersonSuccess,
            ProjectActions.updatePersonSuccess,
            ProjectActions.deletePersonSuccess,
            ProjectActions.linkPartnerToCRMSuccess,
            ProjectActions.unlinkPartnerToCRMSuccess
         ),
         map(() => ProjectActions.loadPartnerList())
      )
   )

   reloadDocumentsAfterMutation$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.addDocumentSuccess, ProjectActions.updateDocumentSuccess, ProjectActions.deleteDocumentSuccess),
         map(() => ProjectActions.loadDocuments())
      )
   )

   loadTodo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadTodo),
         switchMap((action) =>
            this.projectService.getTodoById(action.id).pipe(
               map((response: Todo) => ProjectActions.loadTodoSuccess({ response })),
               catchError((error) => of(ProjectActions.loadTodoFailed({ error })))
            )
         )
      )
   )

   addTodo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.addTodo),
         switchMap((action) =>
            this.projectService.insertTodo(action.data).pipe(
               map(() => ProjectActions.addTodoSuccess()),
               catchError((error) => of(ProjectActions.addTodoFailed({ error })))
            )
         )
      )
   )

   updateTodo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.updateTodo),
         switchMap((action) =>
            this.projectService.updateTodo(action.id, action.data).pipe(
               map(() => ProjectActions.updateTodoSuccess()),
               catchError((error) => of(ProjectActions.updateTodoFailed({ error })))
            )
         )
      )
   )

   deleteTodo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.deleteTodo),
         switchMap((action) =>
            this.projectService.deleteTodo(action.id).pipe(
               map(() => ProjectActions.deleteTodoSuccess()),
               catchError((error) => of(ProjectActions.deleteTodoFailed({ error })))
            )
         )
      )
   )

   updateTodoPartnerStatus$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.updateTodoPartnerStatus),
         switchMap((action) =>
            this.projectService.updateTodoPartnerStatus(action.todoId, action.params).pipe(
               map(() => ProjectActions.updateTodoPartnerStatusSuccess()),
               catchError((error) => of(ProjectActions.updateTodoPartnerStatusFailed({ error })))
            )
         )
      )
   )

   sendTodoNotification$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.sendTodoNotification),
         switchMap((action) =>
            this.projectService.sendTodoNotification(action.id).pipe(
               map(() => ProjectActions.sendTodoNotificationSuccess()),
               catchError((error) => of(ProjectActions.sendTodoNotificationFailed({ error })))
            )
         )
      )
   )

   updateProjectDetail$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.updateProjectDetail),
         switchMap((action) =>
            this.projectService.updateProjectDetail(action.request).pipe(
               map(() => ProjectActions.updateProjectDetailSuccess()),
               catchError((error) => of(ProjectActions.updateProjectDetailFailed({ error })))
            )
         )
      )
   )

   loadReportingPeriods$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadReportingPeriods),
         switchMap((action) =>
            this.projectService.getReportingPeriods().pipe(
               map((response: AppReportingPeriodResponse) => ProjectActions.loadReportingPeriodsSuccess({ response })),
               catchError((error) => of(ProjectActions.loadReportingPeriodsFailed({ error })))
            )
         )
      )
   )

   addReportingPeriod$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.addReportingPeriod),
         switchMap((action) =>
            this.projectService.addReportingPeriod(action.request).pipe(
               map(() => ProjectActions.addReportingPeriodSuccess()),
               catchError((error) => of(ProjectActions.addReportingPeriodFailed({ error })))
            )
         )
      )
   )
   deleteReportingPeriod$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.deleteReportingPeriod),
         switchMap(() =>
            this.projectService.deleteReportingPeriod().pipe(
               map(() => ProjectActions.deleteReportingPeriodSuccess()),
               catchError((error) => of(ProjectActions.deleteReportingPeriodFailed({ error })))
            )
         )
      )
   )

   linkPartnerToCRM$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.linkPartnerToCRM),
         switchMap((action) =>
            this.projectService.linkPartnerToCRM(action.partnerId, action.organizationId).pipe(
               map(() => ProjectActions.linkPartnerToCRMSuccess()),
               catchError((error) => of(ProjectActions.linkPartnerToCRMFailed({ error })))
            )
         )
      )
   )
   unlinkPartnerToCRM$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.unlinkPartnerToCRM),
         switchMap((action) =>
            this.projectService.unlinkPartnerFromCRM(action.partnerId).pipe(
               map(() => ProjectActions.unlinkPartnerToCRMSuccess()),
               catchError((error) => of(ProjectActions.unlinkPartnerToCRMFailed({ error })))
            )
         )
      )
   )

   loadLogo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadLogo),
         switchMap(() =>
            this.projectService.getLogo().pipe(
               map((response: Blob) => ProjectActions.loadLogoSuccess({ file: response })),
               catchError((error) => of(ProjectActions.loadLogoFailed({ error })))
            )
         )
      )
   )

   uploadLogo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.uploadLogo),
         switchMap((action) =>
            this.projectService.uploadLogo(action.base64Logo).pipe(
               map(() => ProjectActions.uploadLogoSuccess()),
               catchError((error) => of(ProjectActions.uploadLogoFailed({ error })))
            )
         )
      )
   )

   deleteLogo$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.deleteLogo),
         switchMap(() =>
            this.projectService.deleteLogo().pipe(
               map(() => ProjectActions.deleteLogoSuccess()),
               catchError((error) => of(ProjectActions.deleteLogoFailed({ error })))
            )
         )
      )
   )

   loadDocuments$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.loadDocuments),
         switchMap(() =>
            this.projectService.getDocuments().pipe(
               map((response) => ProjectActions.loadDocumentsSuccess({ response })),
               catchError((error) => of(ProjectActions.loadDocumentsFailed({ error })))
            )
         )
      )
   )

   addDocument$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.addDocument),
         switchMap((action) =>
            this.projectService.addDocument(action.document).pipe(
               map(() => ProjectActions.addDocumentSuccess()),
               catchError((error) => of(ProjectActions.addDocumentFailed({ error })))
            )
         )
      )
   )

   updateDocument$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.updateDocument),
         switchMap((action) =>
            this.projectService.updateDocument(action.document).pipe(
               map(() => ProjectActions.updateDocumentSuccess()),
               catchError((error) => of(ProjectActions.updateDocumentFailed({ error })))
            )
         )
      )
   )

   deleteDocument$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.deleteDocument),
         switchMap((action) =>
            this.projectService.deleteDocument(action.id).pipe(
               map(() => ProjectActions.deleteDocumentSuccess()),
               catchError((error) => of(ProjectActions.deleteDocumentFailed({ error })))
            )
         )
      )
   )

   sendTodoNotificationToPerson$ = createEffect(() =>
      this.actions$.pipe(
         ofType(ProjectActions.sendNotificationToPerson),
         switchMap((action) =>
            this.projectService.sendTodoNotificationToPerson(action.partnerId, action.personId).pipe(
               map(() => ProjectActions.sendNotificationToPersonSuccess()),
               catchError((error) => of(ProjectActions.sendNotificationToPersonFailed({ error })))
            )
         )
      )
   )

   constructor(
      private actions$: Actions,
      private projectService: ProjectService,
      private store: Store<AppState>,
      private coreService: AppCoreService,
      private spinnerService: AppSpinnerService,
      private notificationService: AppNotificationService,
      private errorHandlerService: AppErrorHandlerService,
      private fileDownloadService: AppFileDownloadService
   ) {}

   private _actionsForSpinnerHide = [
      ProjectActions.updatePartnerOrderSuccess,
      ProjectActions.addPartnerSuccess,
      ProjectActions.updatePartnerSuccess,
      ProjectActions.deletePartnerSuccess,
      ProjectActions.addPersonSuccess,
      ProjectActions.updatePersonSuccess,
      ProjectActions.deletePersonSuccess,
      ProjectActions.addTodoSuccess,
      ProjectActions.updateTodoSuccess,
      ProjectActions.deleteTodoSuccess,
      ProjectActions.updateTodoPartnerStatusSuccess,
      ProjectActions.sendTodoNotificationSuccess,
      ProjectActions.deletePartnerFailed,
      ProjectActions.deletePersonFailed,
      ProjectActions.updatePartnerOrderFailed,
      ProjectActions.addTodoFailed,
      ProjectActions.updateTodoFailed,
      ProjectActions.deleteTodoFailed,
      ProjectActions.updateTodoPartnerStatusFailed,
      ProjectActions.sendTodoNotificationFailed,
      ProjectActions.addPartnerFailed,
      ProjectActions.updatePartnerFailed,
      ProjectActions.addPersonFailed,
      ProjectActions.updatePersonFailed,
      ProjectActions.loadProjectTodoFailed,
      ProjectActions.updateProjectDetailSuccess,
      ProjectActions.updateProjectDetailFailed,
      ProjectActions.linkPartnerToCRMSuccess,
      ProjectActions.linkPartnerToCRMFailed,
      ProjectActions.unlinkPartnerToCRMSuccess,
      ProjectActions.unlinkPartnerToCRMFailed,
      ProjectActions.loadPartnerListSuccess,
      ProjectActions.loadPartnerListFailed,
      ProjectActions.sendNotificationToPersonSuccess,
      ProjectActions.sendNotificationToPersonFailed
   ]

   listenAllSpinnerHideAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerHide),
            map(() => this.spinnerService.spinnerHide())
         ),
      { dispatch: false }
   )

   private _actionsForSpinnerShow = [
      ProjectActions.addPartner,
      ProjectActions.updatePartner,
      ProjectActions.deletePartner,
      ProjectActions.addPerson,
      ProjectActions.updatePerson,
      ProjectActions.deletePerson,
      ProjectActions.updatePartnerOrder,
      ProjectActions.addTodo,
      ProjectActions.updateTodo,
      ProjectActions.deleteTodo,
      ProjectActions.updateTodoPartnerStatus,
      ProjectActions.sendTodoNotification,
      ProjectActions.updateProjectDetail,
      ProjectActions.linkPartnerToCRM,
      ProjectActions.unlinkPartnerToCRM,
      ProjectActions.loadPartnerList,
      ProjectActions.sendNotificationToPerson
   ]

   listenAllSpinnerShowAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerShow),
            map(() => this.spinnerService.spinnerShow())
         ),
      { dispatch: false }
   )

   private _actionsFailedNotification = [
      ProjectActions.loadPartnerListFailed,
      ProjectActions.deletePartnerFailed,
      ProjectActions.deletePersonFailed,
      ProjectActions.updatePartnerOrderFailed,
      ProjectActions.addTodoFailed,
      ProjectActions.updateTodoFailed,
      ProjectActions.deleteTodoFailed,
      ProjectActions.updateTodoPartnerStatusFailed,
      ProjectActions.sendTodoNotificationFailed,
      ProjectActions.addPartnerFailed,
      ProjectActions.updatePartnerFailed,
      ProjectActions.addPersonFailed,
      ProjectActions.updatePersonFailed,
      ProjectActions.loadProjectTodoFailed,
      ProjectActions.loadPartnerFailed,
      ProjectActions.loadTodoFailed,
      ProjectActions.searchPersonsByEmailFailed,
      ProjectActions.updateProjectDetailFailed,
      ProjectActions.loadReportingPeriodsFailed,
      ProjectActions.addReportingPeriodFailed,
      ProjectActions.deleteReportingPeriodFailed,
      ProjectActions.linkPartnerToCRMFailed,
      ProjectActions.unlinkPartnerToCRMFailed,
      ProjectActions.uploadLogoFailed,
      ProjectActions.deleteLogoFailed,
      ProjectActions.loadDocumentsFailed,
      ProjectActions.addDocumentFailed,
      ProjectActions.updateDocumentFailed,
      ProjectActions.deleteDocumentFailed,
      ProjectActions.sendNotificationToPersonFailed
   ]

   listenAllFailedAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsFailedNotification),
            map((errorResponse) => {
               this.errorHandlerService.handleError(errorResponse.error)
            })
         ),
      { dispatch: false }
   )

   private _actionsSuccessNotification = [
      ProjectActions.addPartnerSuccess,
      ProjectActions.updatePartnerSuccess,
      ProjectActions.deletePartnerSuccess,
      ProjectActions.addPersonSuccess,
      ProjectActions.updatePersonSuccess,
      ProjectActions.deletePersonSuccess,
      ProjectActions.addTodoSuccess,
      ProjectActions.updateTodoSuccess,
      ProjectActions.deleteTodoSuccess,
      ProjectActions.updateTodoPartnerStatusSuccess,
      ProjectActions.sendTodoNotificationSuccess,
      ProjectActions.updateProjectDetailSuccess,
      ProjectActions.addReportingPeriodSuccess,
      ProjectActions.deleteReportingPeriodSuccess,
      ProjectActions.linkPartnerToCRMSuccess,
      ProjectActions.unlinkPartnerToCRMSuccess,
      ProjectActions.uploadLogoSuccess,
      ProjectActions.deleteLogoSuccess,
      ProjectActions.addDocumentSuccess,
      ProjectActions.updateDocumentSuccess,
      ProjectActions.deleteDocumentSuccess,
      ProjectActions.sendNotificationToPersonSuccess
   ]

   listenAllSuccessAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsSuccessNotification),
            map(() => {
               this.notificationService.notify(NotificationType.Success)
               this.coreService.closeDialog()
            })
         ),
      { dispatch: false }
   )
}
